import { Species, Reaction } from '../../types/chemistry';
import { ActiveChemistryConfiguration } from '../../store/simulationStore';

/**
 * Color inheritance hierarchy: Reaction → Species → Element (default)
 * Higher levels override lower levels.
 */

interface ColorInfo {
    color: string;
    source: 'reaction' | 'species' | 'element' | 'default';
    inherited: boolean;
}

/**
 * Get the effective color for a species based on the color hierarchy.
 * Priority: Species color → Reaction color (if species in reaction) → Element color
 */
export const getEffectiveColor = (
    speciesId: string,
    speciesName: string,
    reactionColors: Record<string, string>,
    speciesColors: Record<string, string>,
    elementColors: Record<string, string>,
    activeChemistryConfig: ActiveChemistryConfiguration,
    availableReactions: Reaction[]
): ColorInfo => {
    // Check if species has its own color
    if (speciesColors[speciesId]) {
        return { color: speciesColors[speciesId], source: 'species', inherited: false };
    }

    // Check if species inherits from reaction
    for (const reaction of activeChemistryConfig.reactions) {
        const reactionDef = availableReactions.find((r) => r.id === reaction.reactionDefId);
        if (reactionDef && reactionColors[reaction.id]) {
            const speciesInReaction = [
                ...reactionDef.reactants.map((r) => r.speciesId),
                ...reactionDef.products.map((p) => p.speciesId)
            ];
            if (speciesInReaction.includes(speciesId)) {
                return { color: reactionColors[reaction.id], source: 'reaction', inherited: true };
            }
        }
    }

    // Fall back to element color
    const elementColor = elementColors[speciesName] || '#ffffff';
    return { color: elementColor, source: 'element', inherited: true };
};

/**
 * Get the effective color for an element based on the color hierarchy.
 * Priority: Element color → Species color (if element in species) → Reaction color → Default
 */
export const getEffectiveElementColor = (
    element: string,
    elementColors: Record<string, string>,
    reactionColors: Record<string, string>,
    speciesColors: Record<string, string>,
    activeChemistryConfig: ActiveChemistryConfiguration,
    availableReactions: Reaction[],
    availableSpecies: Species[]
): ColorInfo => {
    // Check if element has its own color
    if (elementColors[element]) {
        return { color: elementColors[element], source: 'element', inherited: false };
    }

    // Check if element inherits from any reaction
    for (const reaction of activeChemistryConfig.reactions) {
        const reactionDef = availableReactions.find((r) => r.id === reaction.reactionDefId);
        if (reactionDef && reactionColors[reaction.id]) {
            const speciesInReaction = [
                ...reactionDef.reactants.map((r) => r.speciesId),
                ...reactionDef.products.map((p) => p.speciesId)
            ];

            // Check if any species in this reaction contains this element
            const speciesWithElement = speciesInReaction.filter(speciesId => {
                const species = availableSpecies.find((s) => s.id === speciesId);
                return species && species.name.includes(element);
            });

            if (speciesWithElement.length > 0) {
                return { color: reactionColors[reaction.id], source: 'reaction', inherited: true };
            }
        }
    }

    // Check if element inherits from species colors
    const speciesWithElement = availableSpecies.filter((species) =>
        species.name.includes(element) && speciesColors[species.id]
    );

    if (speciesWithElement.length > 0) {
        return { color: speciesColors[speciesWithElement[0].id], source: 'species', inherited: true };
    }

    // Default fallback - use the default element color
    const defaultElementColors: Record<string, string> = {
        'H': '#FFFFFF',  // White for hydrogen
        'O': '#FF0000',  // Red for oxygen
        'C': '#000000',  // Black for carbon
        'N': '#0000FF',  // Blue for nitrogen
        'Cl': '#00FF00', // Green for chlorine
    };

    return { color: defaultElementColors[element] || '#ffffff', source: 'default', inherited: false };
};
